// The daily quote is deterministic calendar arithmetic, not randomness.
//
// One local date maps to one array position. Consecutive dates walk the whole
// finite corpus before returning to the first entry, so the no-repeat interval
// is exactly the corpus size and never presented as an endless supply.

import { DAILY_QUOTES } from './daily-quotes.ts';
import type { DailyQuote } from './daily-quotes.ts';

const MILLIS_PER_DAY = 24 * 60 * 60 * 1_000;
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

function serialDay(day: string): number {
  const match = ISO_DAY.exec(day);
  if (match === null) throw new Error(`The quote day is not an ISO date: ${day}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const date = Number(match[3]);

  // `Date.UTC(0, ...)` means 1900, so set the full year explicitly. UTC is only
  // an arithmetic frame for an already-resolved calendar value; no instant is
  // being assigned to the user's day here.
  const value = new Date(0);
  value.setUTCHours(0, 0, 0, 0);
  value.setUTCFullYear(year, month - 1, date);
  if (
    value.getUTCFullYear() !== year ||
    value.getUTCMonth() !== month - 1 ||
    value.getUTCDate() !== date
  ) {
    throw new Error(`The quote day does not exist: ${day}`);
  }
  return Math.floor(value.getTime() / MILLIS_PER_DAY);
}

export function quoteForDay(day: string): DailyQuote {
  const index =
    ((serialDay(day) % DAILY_QUOTES.length) + DAILY_QUOTES.length) % DAILY_QUOTES.length;
  const quote = DAILY_QUOTES[index];
  if (quote === undefined) throw new Error('The daily quote corpus is empty.');
  return quote;
}
